import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Goal} from '../../../../../models/goal';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {GoalService} from '../../../../../services/goal/goal.service';

import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {GoalType} from '../../../../../models/goal-type';
import {isNumber} from 'chart.js/helpers';
import {GoalTypeService} from '../../../../../services/goal-type/goal-type.service';
import {forkJoin, Observable} from 'rxjs';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';

@Component({
  selector: 'app-goal-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    ConfirmDirective,
    FormsModule,
    InputSwitchComponent
],
  templateUrl: './goal-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './goal-form.component.scss'
})
@IsGranted(['ROLE_API_GOAL_WRITE', 'GOAL_VIEW'])
@MetaTitle('Goal form')
export class GoalFormComponent implements OnInit {
  goal: Goal | null = null;
  toDelete: GoalType[] = [];
  toUpdate: GoalType[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    identifier: new FormControl('', Validators.required),
    addToRevenue: new FormControl(false, Validators.required),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private goalService: GoalService,
    private goalTypeService: GoalTypeService
  ) {
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (id) {
      this.loader.startLoading();
      this.goalService.findOneById(id, {groups: 'advertiser,types'}).subscribe((goal: Goal) => {
        this.goal = goal;

        this.form.patchValue({
          name: this.goal.name,
          identifier: this.goal.identifier,
          addToRevenue: this.goal.addToRevenue,
        });
        this.loader.stopLoading();
      });
    }
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.isCreate()) {
      this.goalService.create(params).subscribe((response) => {
        this.goal = new Goal();
        this.goal.id = response.id;

        this.updateTypes().subscribe(() => {
          this.loader.stopLoading();
          this.snackbar.success('Goal has been created.');
          this.router.navigateByUrl('/configuration/goals').then();
        });
      });
    } else if (this.goal?.id) {
      this.goalService.update(this.goal.id, params).subscribe(() => {

        this.updateTypes().subscribe(() => {
          this.loader.stopLoading();
          this.snackbar.success('Goal has been modified.');
          this.router.navigateByUrl('/configuration/goals').then();
        });
      });
    }
  }

  updateTypes(): Observable<void> {
    return new Observable<void>(observer => {
      const requests: Observable<void>[] = [];

      this.toDelete.forEach((type: GoalType) => {
        if (type.id) {
          requests.push(this.goalTypeService.delete(type.id));
        }
      });

      this.toUpdate.forEach((type: GoalType) => {
        if (type.id) {
          const updateParams: any = {
            name: type.name,
            identifier: type.identifier
          };

          requests.push(this.goalTypeService.update(type.id, updateParams));
        }
      });

      this.goal?.types.forEach((type: GoalType) => {
        if (!type.id) {
          const createParams: any = {
            goalId: this.goal?.id,
            name: type.name,
            identifier: type.identifier
          };

          requests.push(this.goalTypeService.create(createParams));
        }
      });

      if (requests.length > 0) {
        forkJoin(requests).subscribe(() => {
          observer.next();
        });
      } else {
        observer.next();
      }
    });
  }

  isCreate(): boolean {
    return !this.goal;
  }

  addType(): void {
    const type = new GoalType();
    this.goal?.types.push(type);
  }

  updateType(type: GoalType): void {
    if (!this.toUpdate.includes(type)) {
      this.toUpdate.push(type);
    }
  }

  deleteType(type: GoalType): void {
    const i = this.goal?.types.indexOf(type);
    if (isNumber(i) && i >= 0) {
      this.goal?.types.splice(i, 1);

      if (type.id) {
        this.toDelete.push(type);
      }
    }
  }

  getTypeCount(): number {
    return this.goal?.types.length ?? 0;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_GOAL_WRITE']);
  }
}
